/**
 * Which sprint blocks the reader has opened.
 *
 * Two things were wrong with the state this replaces. It lived in `useState`,
 * so every remount of the Overview threw the reader's choice away; and its
 * default only collapsed `completed` and `cancelled` sprints, which in this
 * product is none of them — so a board's Overview opened at three screens tall
 * with the whole issue list expanded before the reader had asked for anything.
 *
 * Collapsed is now the default for every status, and what is remembered is the
 * EXCEPTION — the ids the reader expanded. That keeps the stored value small
 * (usually empty), makes the default cost no write at all, and means a sprint
 * nobody has touched needs no entry to behave correctly.
 *
 * The storage is a parameter rather than a global so the rule is testable: the
 * frontend test environment is `node` and has no `localStorage`.
 */

const STORAGE_KEY = 'hygge.boards.sprint-expanded';

/** Enough for any board a person actually reads; small enough not to bloat the entry. */
export const MAX_REMEMBERED_SPRINTS = 100;

/** The slice of `Storage` this needs. Anything with these two methods will do. */
export interface SprintCollapseStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/**
 * `localStorage`, or nothing.
 *
 * Reading the property itself throws in a browser configured to block site
 * data, so even the lookup is guarded — a blocked preference must cost the
 * reader a default, never a blank board.
 */
function defaultStorage(): SprintCollapseStorage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}

/** The remembered ids, or an empty list for anything that isn't a list of strings. */
function readExpanded(storage: SprintCollapseStorage | null): string[] {
  if (!storage) return [];
  try {
    const parsed: unknown = JSON.parse(storage.getItem(STORAGE_KEY) ?? '[]');
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((id): id is string => typeof id === 'string');
  } catch {
    return [];
  }
}

/** False for a sprint nobody has expanded — collapsed is the default for every status. */
export function isSprintExpanded(
  sprintId: string,
  storage: SprintCollapseStorage | null = defaultStorage(),
): boolean {
  return readExpanded(storage).includes(sprintId);
}

export function setSprintExpanded(
  sprintId: string,
  expanded: boolean,
  storage: SprintCollapseStorage | null = defaultStorage(),
): void {
  if (!storage) return;

  const without = readExpanded(storage).filter((id) => id !== sprintId);
  // Most recent last, so the cap drops the sprint opened longest ago.
  const next = expanded ? [...without, sprintId].slice(-MAX_REMEMBERED_SPRINTS) : without;

  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    /* storage full or blocked — the preference just won't survive the remount */
  }
}
